/**
 * WebhooksOutbox — Iter Feb 2026 R43 · Phase 5a.
 *
 * Webhook subscription CRUD + delivery log. This is the Make/n8n
 * escape hatch — any external tool can register a POST endpoint,
 * pick events, verify HMAC signature, and get retried-on-failure
 * delivery with a full audit trail.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  Webhook, Plus, RefreshCw, KeyRound, Send, CheckCircle2, XCircle, Clock,
  Trash2, PauseCircle, PlayCircle, Copy, RotateCw,
} from "lucide-react";

export default function WebhooksOutbox() {
  const { activeWorkspaceId } = useAuth();
  const [rows, setRows] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [openCreate, setOpenCreate] = useState(false);
  const [detail, setDetail] = useState(null); // { sub, deliveries[] }
  const [secretJustCreated, setSecretJustCreated] = useState(null);

  const load = useCallback(async () => {
    if (!activeWorkspaceId) return;
    try {
      const [{ data: a }, { data: b }] = await Promise.all([
        api.get(`/workspaces/${activeWorkspaceId}/webhooks`),
        api.get(`/workspaces/${activeWorkspaceId}/webhooks/events`),
      ]);
      setRows(a.rows || []);
      setCatalog(b.events || []);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load")); }
  }, [activeWorkspaceId]);
  useEffect(() => { load(); }, [load]);

  const openDetail = async (sub) => {
    try {
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/webhooks/${sub.id}/deliveries`);
      setDetail({ sub, deliveries: data.rows || [] });
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load deliveries")); }
  };

  const testFire = async (sub) => {
    try {
      await api.post(`/workspaces/${activeWorkspaceId}/webhooks/${sub.id}/test`);
      toast.success("Test event fired — refresh deliveries in a moment");
      setTimeout(() => openDetail(sub), 2000);
    } catch (e) { toast.error(apiErrorMessage(e, "Test fire failed")); }
  };

  const pauseResume = async (sub) => {
    const next = sub.status === "active" ? "paused" : "active";
    try {
      await api.patch(`/workspaces/${activeWorkspaceId}/webhooks/${sub.id}`, { status: next });
      toast.success(`Subscription ${next}`);
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Update failed")); }
  };

  const remove = async (sub) => {
    if (!window.confirm(`Delete "${sub.name}"?`)) return;
    try {
      await api.delete(`/workspaces/${activeWorkspaceId}/webhooks/${sub.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };

  const rotate = async (sub) => {
    try {
      const { data } = await api.post(`/workspaces/${activeWorkspaceId}/webhooks/${sub.id}/rotate-secret`);
      setSecretJustCreated({ id: sub.id, secret: data.secret, name: sub.name });
      toast.success("Secret rotated — copy it now");
    } catch (e) { toast.error(apiErrorMessage(e, "Rotate failed")); }
  };

  const retry = async (sub, did) => {
    try {
      await api.post(`/workspaces/${activeWorkspaceId}/webhooks/deliveries/${did}/retry`);
      toast.success("Retry scheduled");
      setTimeout(() => openDetail(sub), 1500);
    } catch (e) { toast.error(apiErrorMessage(e, "Retry failed")); }
  };

  return (
    <div className="flex flex-col h-full" data-testid="webhooks-outbox-page">
      <PageHeader
        eyebrow="Workspace · Integrations"
        title="Outbound Webhooks"
        description="Any external tool (Make, n8n, Zapier, your own service) can subscribe to Acquilix events. Each delivery is HMAC-SHA256 signed and retried up to 5× with exponential backoff."
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" className="rounded-none h-9" onClick={load} data-testid="wh-refresh">
              <RefreshCw className="w-4 h-4 mr-1" /> Refresh
            </Button>
            <Button className="rounded-none h-9" onClick={() => setOpenCreate(true)} data-testid="wh-new">
              <Plus className="w-4 h-4 mr-1" /> New subscription
            </Button>
          </div>
        }
      />
      <div className="flex-1 overflow-auto px-4 sm:px-6 md:px-10 py-4 space-y-4">
        {rows.length === 0 && (
          <div className="border-2 border-dashed p-10 text-center space-y-2">
            <Webhook className="w-10 h-10 text-neutral-400 mx-auto" />
            <div className="font-semibold">No webhook subscriptions yet</div>
            <div className="text-sm text-muted-foreground">Create one to push Acquilix events into any external system in real time.</div>
            <Button className="rounded-none" onClick={() => setOpenCreate(true)}>
              <Plus className="w-4 h-4 mr-1" /> Create the first subscription
            </Button>
          </div>
        )}
        {rows.length > 0 && (
          <div className="border">
            <table className="w-full text-xs">
              <thead className="bg-neutral-50 text-[10px] uppercase tracking-widest text-muted-foreground">
                <tr className="border-b">
                  <th className="text-left px-2 py-1.5">Name</th>
                  <th className="text-left px-2 py-1.5">URL</th>
                  <th className="text-left px-2 py-1.5">Events</th>
                  <th className="text-right px-2 py-1.5">Success</th>
                  <th className="text-right px-2 py-1.5">Fail</th>
                  <th className="text-right px-2 py-1.5">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.id} className="border-b hover:bg-neutral-50" data-testid={`wh-row-${r.id}`}>
                    <td className="px-2 py-2 font-semibold">
                      <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1.5 ${r.status === "active" ? "bg-emerald-500" : "bg-neutral-400"}`} />
                      {r.name}
                    </td>
                    <td className="px-2 py-2 max-w-[240px] truncate font-mono" title={r.url}>{r.url}</td>
                    <td className="px-2 py-2 max-w-[240px]">
                      {(r.events || []).slice(0, 3).map(e => <code key={e} className="inline-block text-[10px] border px-1 mr-1">{e}</code>)}
                      {(r.events || []).length > 3 && <span className="text-[10px] text-muted-foreground">+{r.events.length - 3}</span>}
                    </td>
                    <td className="px-2 py-2 text-right text-emerald-600 font-mono">{r.success_count || 0}</td>
                    <td className="px-2 py-2 text-right text-rose-600 font-mono">{r.failure_count || 0}</td>
                    <td className="px-2 py-2 text-right">
                      <div className="inline-flex items-center gap-1">
                        <Button size="sm" variant="ghost" className="h-7 rounded-none px-1.5" onClick={() => openDetail(r)} title="View deliveries" data-testid={`wh-detail-${r.id}`}>
                          <Clock className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 rounded-none px-1.5" onClick={() => testFire(r)} title="Fire test event" data-testid={`wh-test-${r.id}`}>
                          <Send className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 rounded-none px-1.5" onClick={() => rotate(r)} title="Rotate secret" data-testid={`wh-rotate-${r.id}`}>
                          <RotateCw className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 rounded-none px-1.5" onClick={() => pauseResume(r)} title={r.status === "active" ? "Pause" : "Resume"}>
                          {r.status === "active" ? <PauseCircle className="w-3.5 h-3.5" /> : <PlayCircle className="w-3.5 h-3.5" />}
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 rounded-none px-1.5 text-rose-600" onClick={() => remove(r)} title="Delete">
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Signature guide */}
        <div className="border p-3 text-xs bg-neutral-50 space-y-2">
          <div className="font-semibold flex items-center gap-1.5"><KeyRound className="w-3.5 h-3.5" /> How to verify signatures</div>
          <p className="text-muted-foreground">
            Every delivery includes <code className="bg-white border px-1">X-Acquilix-Signature: t=&lt;epoch&gt;,v1=&lt;hex&gt;</code>.
            Recompute v1 = HMAC-SHA256(secret, <code className="bg-white border px-1">t + &quot;.&quot; + raw_body</code>) and compare.
            Reject requests where <code className="bg-white border px-1">t</code> is more than 5 minutes old.
          </p>
          <pre className="text-[10px] bg-neutral-900 text-neutral-100 p-2 overflow-auto">
{`# Python verify
import hmac, hashlib
def verify(secret, raw_body, header):
    t, v1 = dict(x.split("=",1) for x in header.split(",")).values()
    mac = hmac.new(secret.encode(), f"{t}.".encode()+raw_body, hashlib.sha256).hexdigest()
    return hmac.compare_digest(mac, v1)`}
          </pre>
        </div>
      </div>

      {openCreate && (
        <CreateSubscriptionDialog
          wsId={activeWorkspaceId}
          catalog={catalog}
          onClose={() => setOpenCreate(false)}
          onCreated={(secret, sub) => {
            setOpenCreate(false);
            setSecretJustCreated({ id: sub.id, secret, name: sub.name });
            load();
          }}
        />
      )}
      {secretJustCreated && (
        <SecretShownDialog data={secretJustCreated} onClose={() => setSecretJustCreated(null)} />
      )}
      {detail && (
        <DeliveriesDialog
          wsId={activeWorkspaceId}
          detail={detail}
          onClose={() => setDetail(null)}
          onRetry={(did) => retry(detail.sub, did)}
        />
      )}
    </div>
  );
}

function CreateSubscriptionDialog({ wsId, catalog, onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", url: "", description: "" });
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const toggle = (ev) => setSelected(s => {
    const n = new Set(s);
    if (n.has(ev)) n.delete(ev); else n.add(ev);
    return n;
  });
  const submit = async () => {
    if (!form.name.trim() || !form.url.trim()) return toast.error("Name + URL required");
    if (selected.size === 0) return toast.error("Pick at least one event");
    setBusy(true);
    try {
      const { data } = await api.post(`/workspaces/${wsId}/webhooks`, {
        name: form.name.trim(),
        url: form.url.trim(),
        description: form.description.trim() || null,
        events: Array.from(selected),
      });
      onCreated(data.secret, data);
    } catch (e) { toast.error(apiErrorMessage(e, "Create failed")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !busy && !v && onClose()}>
      <DialogContent className="max-w-lg rounded-none">
        <DialogHeader>
          <DialogTitle>New webhook subscription</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Name</Label>
            <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="h-9 rounded-none" data-testid="wh-form-name" />
          </div>
          <div>
            <Label className="text-xs">URL (must be https://)</Label>
            <Input value={form.url} onChange={e => setForm({ ...form, url: e.target.value })} placeholder="https://hooks.example.com/acquilix" className="h-9 rounded-none font-mono text-xs" data-testid="wh-form-url" />
          </div>
          <div>
            <Label className="text-xs">Events</Label>
            <div className="border max-h-56 overflow-auto">
              {catalog.map(e => (
                <label key={e.event} className="flex items-center gap-2 px-2 py-1.5 border-b text-xs hover:bg-neutral-50 cursor-pointer">
                  <input type="checkbox" checked={selected.has(e.event)} onChange={() => toggle(e.event)} data-testid={`wh-form-ev-${e.event}`} />
                  <code className="font-mono">{e.event}</code>
                  <span className="text-muted-foreground flex-1">{e.description}</span>
                  {e.count_30d > 0 && <span className="text-[10px] bg-neutral-100 border px-1">{e.count_30d}/30d</span>}
                </label>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs">Description (optional)</Label>
            <Textarea rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="rounded-none text-sm" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button className="rounded-none" onClick={submit} disabled={busy} data-testid="wh-form-submit">
            {busy ? "Creating…" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SecretShownDialog({ data, onClose }) {
  const copy = async () => {
    try { await navigator.clipboard.writeText(data.secret); toast.success("Secret copied"); }
    catch { toast.error("Clipboard blocked"); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md rounded-none">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><KeyRound className="w-4 h-4" /> Signing secret for {data.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          <div className="border-l-4 border-amber-400 bg-amber-50 p-2 text-xs text-amber-800">
            <b>Save this now</b> — Acquilix will never show it again. Store it in the receiving system as an env var (e.g. <code>ACQUILIX_WEBHOOK_SECRET</code>).
          </div>
          <div className="flex items-center gap-2">
            <code className="border p-2 flex-1 text-xs break-all bg-neutral-100 select-all">{data.secret}</code>
            <Button size="sm" className="rounded-none" onClick={copy} data-testid="wh-secret-copy"><Copy className="w-4 h-4" /></Button>
          </div>
        </div>
        <DialogFooter>
          <Button className="rounded-none" onClick={onClose}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeliveriesDialog({ wsId, detail, onClose, onRetry }) {
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl rounded-none">
        <DialogHeader>
          <DialogTitle>Deliveries · {detail.sub.name}</DialogTitle>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-auto border" data-testid="wh-deliveries-list">
          <table className="w-full text-xs">
            <thead className="text-[10px] uppercase tracking-widest text-muted-foreground bg-neutral-50">
              <tr className="border-b">
                <th className="text-left px-2 py-1">Event</th>
                <th className="text-left px-2 py-1">Time</th>
                <th className="text-right px-2 py-1">Attempts</th>
                <th className="text-right px-2 py-1">HTTP</th>
                <th className="text-left px-2 py-1">Status</th>
                <th className="text-right px-2 py-1"></th>
              </tr>
            </thead>
            <tbody>
              {detail.deliveries.length === 0 && (
                <tr><td colSpan={6} className="py-4 text-center text-muted-foreground">No deliveries yet.</td></tr>
              )}
              {detail.deliveries.map(d => {
                const Icon = d.status === "delivered" ? CheckCircle2 : d.status === "abandoned" ? XCircle : Clock;
                const color = d.status === "delivered" ? "text-emerald-600" : d.status === "abandoned" ? "text-rose-600" : "text-amber-600";
                return (
                  <tr key={d.id} className="border-b" data-testid={`wh-del-${d.id}`}>
                    <td className="px-2 py-1 font-mono text-[10px]">{d.event}</td>
                    <td className="px-2 py-1 text-[10px]">{d.created_at?.slice(0, 19).replace("T", " ")}</td>
                    <td className="px-2 py-1 text-right font-mono">{d.attempts}</td>
                    <td className="px-2 py-1 text-right font-mono">{d.last_status_code || "—"}</td>
                    <td className="px-2 py-1">
                      <span className={`inline-flex items-center gap-1 ${color}`}>
                        <Icon className="w-3 h-3" /> {d.status}
                      </span>
                    </td>
                    <td className="px-2 py-1 text-right">
                      {d.status !== "delivered" && (
                        <Button size="sm" variant="outline" className="rounded-none h-6 text-[10px]" onClick={() => onRetry(d.id)}>Retry</Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
